import { useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button, Icon, Label, Message, Modal, Popup, Table } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery } from "api"
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor"
import { IdeaBusinessProfileChip } from "components/lib/Chips"
import CircularProgress from "components/lib/CircularProgress"
import IdeaPdfExport from "components/lib/IdeaPdfExport"
import { HandledIdeaPreview } from "components/lib/Ideas"
import { useTrayStatus } from "components/lib/Notifications"
import ProductTour from "components/lib/ProductTour"
import { FakeLink, TooltipButton } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

export const AssessmentClosureInfo = ({
  ideaTemplate,
  assessmentRound,
}: {
  ideaTemplate?: OpenAPI.Schemas["IdeaTemplate"]
  assessmentRound?: OpenAPI.Schemas["IdeaTemplate"]["assessments"]["rounds"][number]
}) => {
  const { t } = useTranslation()

  if (!ideaTemplate) return null
  const assessmentDeadline =
    (assessmentRound.id === "legacy" ? ideaTemplate.assessmentDeadline : assessmentRound.assessmentDeadline) || false
  const assessmentClosed =
    (assessmentRound.id === "legacy" ? ideaTemplate.assessmentClosed : assessmentRound.assessmentClosed) || false

  if (assessmentClosed) {
    return (
      <Message warning size="small" style={{ marginBottom: 25 }}>
        <p>
          <b>Assessment closed:</b> Assessment is now closed for this {t("generic.challenge")}. You can continue to view
          your assigned ideas and previous assessments. If you think this is in error, please contact an administrator.
        </p>
      </Message>
    )
  }
  if (assessmentDeadline) {
    return (
      <Message info size="small" style={{ marginBottom: 25 }}>
        <p>
          <b>Assessment deadline:</b> Assessment for this {t("generic.challenge")} has a deadline of{" "}
          <b>{moment(assessmentDeadline).format("HH:mm on DD/MM/YY")}</b>. You cannot edit or submit assessments after
          this date.
        </p>
      </Message>
    )
  }
  return null
}

/* Note that myAssignedAssessments is a list of ideas, each with an assessment object, which might contain an assessments array. Each entry in the assessments array is an assessment made by an individual assessor, which might contain a (deeper) assessment object, which references individual idea sections.
 * Example myAssignedAssessments: [
 * {
 *    name: 'Idea name',
 *    _id: 'IdeaID',
 *    assessments: [ // array of assessor's assessments
 *      {
 *        score: 75, // total calculated score across this assessor based on total available
 *        assessorUser: userObject,
 *        assessment: {
 *          'section1ID': {
 *            score: 15, // assessor's score for this section,
 *            notes: 'Notes for this section'
 *          }, ...
 *        },
 *      }, ...
 *    ]
 *  }, ...
 * ]
 *
 * Note that whilst other assessor's assessments may be present in the DB and their names included in the array, if scoped to a single assessor then the total calculated score will only take into account the scoped assessment.
 */

type YourAssessmentRoundProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"]
  assessmentRound: OpenAPI.Schemas["IdeaTemplate"]["assessments"]["rounds"][number]
  demoingTour?: string
}

const YourAssessmentRound = ({ challenge, assessmentRound, demoingTour }: YourAssessmentRoundProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const user = useAppSelector((state) => state.user)
  const urlOrganisation = useAppSelector(
    (state: { organisations: { urlOrganisation: OpenAPI.Schemas["Organisation"] | null } }) =>
      state?.organisations?.urlOrganisation,
  )

  const [searchParams] = useSearchParams()
  const [previewId, setPreviewId] = useState(searchParams.has("idea") ? searchParams.get("idea") : "")
  const [tourCheckLoaded, setTourCheckLoaded] = useState(false)

  const [_, setTrayStatus] = useTrayStatus()
  const [assessmentSubmissionModalOpen, setAssessmentSubmissionModalOpen] = useState(false)
  const { setAssessmentRound } = useAssessmentEditor(undefined)
  const { ownerIdeaTemplate } = challenge || {}

  // If we are about to run the assessment tour, we do not display the idea preview
  const getTour = useCallback(() => {
    if ((user?.ownerOrganisation?._id || urlOrganisation) && !theme.sizes.isMobile) {
      api.organisations.getProductTour(
        user?.ownerOrganisation?._id || urlOrganisation,
        "assessment",
        ({ success, userHasCompletedTour }) => {
          if (success && (!userHasCompletedTour || demoingTour === "assessment")) {
            navigate({ search: "" }, { replace: true })
            setPreviewId("")
          }
          setTourCheckLoaded(true)
        },
        () => setTourCheckLoaded(true),
      )
    } else {
      setTourCheckLoaded(true)
    }
  }, [user, demoingTour, urlOrganisation, navigate, theme.sizes.isMobile])

  useEffect(() => {
    getTour()
  }, [getTour])

  const onModalClose = useCallback(() => {
    navigate({ search: "" }, { replace: true })
    setPreviewId("")
  }, [navigate])

  const assessmentClosed =
    (assessmentRound.id === "legacy" ? ownerIdeaTemplate.assessmentClosed : assessmentRound.assessmentClosed) || false

  const assignedAssessmentQuery = useQuery({
    queryKey: ["challenges", challenge?._id, "assessments"],
    queryFn: asQuery(api.challenges.getAssignedAssessments, {
      params: [challenge?._id],
    }),
    enabled: !!user?._id,
  })
  const allAssignedAssessmentIdeas = assignedAssessmentQuery.data?.ideas || []
  const currentRoundAssessmentIdeas = useMemo(() => {
    return allAssignedAssessmentIdeas.filter((idea) =>
      (idea.assessments || []).some(
        (assessment) =>
          assessment.user === user?._id && (assessment.assessmentRoundId || "legacy") === assessmentRound.id,
      ),
    )
  }, [user?._id, allAssignedAssessmentIdeas, assessmentRound.id])
  const assessmentIdeaIds = useMemo(() => currentRoundAssessmentIdeas.map((a) => a._id), [currentRoundAssessmentIdeas])

  const assignedAssessments = useMemo(() => {
    return currentRoundAssessmentIdeas
      ?.flatMap((idea) =>
        (idea.assessments || [])?.map((assessment) =>
          assessment.user === user?._id && (assessment.assessmentRoundId || "legacy") === assessmentRound.id
            ? {
                idea,
                ...assessment,
              }
            : null,
        ),
      )
      .filter(Boolean)
  }, [user?._id, currentRoundAssessmentIdeas, assessmentRound.id])

  const shouldShowTour = useMemo(
    () => !!challenge?._id && !!currentRoundAssessmentIdeas.length,
    [challenge, currentRoundAssessmentIdeas],
  )
  const tourIdea = useMemo(
    () => (currentRoundAssessmentIdeas.length ? currentRoundAssessmentIdeas[0]._id : ""),
    [currentRoundAssessmentIdeas],
  )

  const currentPreviewIndex = useMemo(() => assessmentIdeaIds.indexOf(previewId), [assessmentIdeaIds, previewId])
  const navigateNextPreviousIdea = useCallback(
    (direction) => {
      if (direction === 1) {
        setPreviewId(assessmentIdeaIds[currentPreviewIndex + 1])
      } else {
        setPreviewId(assessmentIdeaIds[currentPreviewIndex - 1])
      }
    },
    [currentPreviewIndex, assessmentIdeaIds],
  )

  const showBusinessProfile = useMemo(() => {
    return ownerIdeaTemplate.body.some(
      (section) => section.type === "businessProfile" && section.visibility !== "managers",
    )
  }, [ownerIdeaTemplate])

  const totalAssessableFields = useMemo(() => {
    return (
      ownerIdeaTemplate?.body
        ?.filter(
          (section) => section.type === "assessment" && (section.assessmentRoundId || "legacy") === assessmentRound.id,
        )
        .reduce(
          (count, section) =>
            count + section.fields.filter((field) => field.type === "assessment" || field.type === "dropdown").length,
          0,
        ) || 0
    )
  }, [ownerIdeaTemplate, assessmentRound.id])

  const unsubmittedAssessments = useMemo(
    () =>
      currentRoundAssessmentIdeas.filter((assessment) =>
        assessment.assessments.find((ideaAssessment) => ideaAssessment.isSubmitted !== true),
      ),
    [currentRoundAssessmentIdeas],
  )

  const submissableAssessments = useMemo(
    () =>
      unsubmittedAssessments.filter((assessment) => {
        const myAssessment = assessment.assessments?.find(
          (ideaAssessment) =>
            (ideaAssessment.assessmentRoundId || "legacy") === assessmentRound.id && ideaAssessment.user === user?._id,
        )
        const assessmentIsSubmissable = util.assessmentIsSubmissable(myAssessment, ownerIdeaTemplate)
        return assessmentIsSubmissable
      }),
    [unsubmittedAssessments, user?._id, ownerIdeaTemplate, assessmentRound.id],
  )

  const unsubmittableAssessments = useMemo(
    () =>
      unsubmittedAssessments.filter((assessment) => {
        const myAssessment = assessment.assessments?.find((ideaAssessment) => ideaAssessment.user === user?._id)
        const assessmentIsSubmissable = util.assessmentIsSubmissable(myAssessment, ownerIdeaTemplate)
        return !assessmentIsSubmissable
      }),
    [unsubmittedAssessments, user?._id, ownerIdeaTemplate],
  )

  const downloadReport = () => {
    const challengeName = challenge?.name || "Challenge"

    let displayName = `${challengeName} assessment report`
    if (assessmentRound) {
      displayName += ` - ${assessmentRound.name}`
    }

    api.users.createDownload(
      user._id,
      {
        downloadType: "userAssessmentXlsxExport",
        displayName: `${displayName}.xlsx`,
        challenge: challenge._id,
        assessmentRoundId: assessmentRound?.id,
      },
      (download) => {
        api.queryClient.setQueryData(["get", "users", "downloads"], (prev: any) => ({
          ...prev,
          pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
        }))
        setTrayStatus({ open: true, activeIndex: 1 })
      },
      () => {},
    )
  }

  const submitAssessment = (ideaId) => {
    util
      .confirm(
        "Ready to submit your assessment?",
        `Once submitted you will not be able to change your assessment of this ${t("generic.idea")}.`,
      )
      .then(() =>
        api.ideas.submitAssessment(
          ideaId,
          assessmentRound.id,
          () => {
            toast.success("Assessment submitted")
            api.queryClient.invalidateQueries({
              queryKey: ["challenges", challenge?._id, "assessments"],
            })
          },
          (err) => {
            toast.error(`Unable to submit your assessment: ${err.message}`)
          },
        ),
      )
      .catch(() => {})
  }

  const submitAllAssessments = useCallback(() => {
    util
      .confirm(
        "Ready to submit your assessments?",
        `Once submitted you will not be able to change any of your assessments in this ${t("generic.challenge")}. Only completed assessments will be submitted.`,
      )
      .then(() => {
        api.challenges.submitAllAssessments(
          challenge?._id,
          assessmentRound.id,
          ({ submittedAssessmentIdeaIds }) => {
            toast.success("Assessments submitted")
            api.queryClient.setQueryData(["challenges", challenge?._id, "assessments"], (oldData: any) => {
              if (!oldData) return oldData
              const newIdeas = oldData.ideas.map((ideaAssessment) => {
                if (submittedAssessmentIdeaIds.indexOf(ideaAssessment._id) > -1) {
                  return {
                    ...ideaAssessment,
                    assessments: ideaAssessment.assessments.map((prev) => ({ ...prev, isSubmitted: true })),
                  }
                }
                return ideaAssessment
              })
              return { ...oldData, ideas: newIdeas }
            })
          },
          (err) => toast.error(`Unable to submit your assessment: ${err.message}`),
        )
      })
      .catch(() => {})
  }, [challenge?._id, assessmentRound.id, t])

  const checkAllSubmissibility = useCallback(() => {
    if (submissableAssessments.length === unsubmittedAssessments.length) {
      submitAllAssessments()
    } else {
      setAssessmentSubmissionModalOpen(true)
    }
  }, [submitAllAssessments, unsubmittedAssessments, submissableAssessments])

  if (assignedAssessments.length === 0) {
    return <Message>No assessments assigned in this round.</Message>
  }

  return (
    <>
      <HandledIdeaPreview
        ideaId={previewId}
        closeModal={() => onModalClose()}
        canGoNextIdea={currentPreviewIndex < currentRoundAssessmentIdeas.length - 1}
        canGoPrevIdea={currentPreviewIndex > 0}
        moveIdea={navigateNextPreviousIdea}
        totalIdeas={currentRoundAssessmentIdeas.length}
        currentIdeaPosition={currentPreviewIndex + 1}
        page={1}
        limit={currentRoundAssessmentIdeas.length}
      />
      <ProductTour
        tour="assessment"
        skipStep={!ownerIdeaTemplate?.assessmentDeadline && ".assessment-tour-step-5"}
        isReady={shouldShowTour}
        customTourFunctions={{
          openIdea: () => setPreviewId(tourIdea),
          closeIdea: () => {
            onModalClose()
          },
        }}
        onComplete={() => setPreviewId("")}
      />
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={assessmentSubmissionModalOpen}
        onClose={() => setAssessmentSubmissionModalOpen(false)}
      >
        <Modal.Header>Assessment submission</Modal.Header>
        <Modal.Content>
          <h4>
            {" "}
            You have{" "}
            {util.pluralise(submissableAssessments?.length || 0, "completed assessment", "completed assessments")} and{" "}
            {util.pluralise(unsubmittableAssessments?.length || 0, "oustanding assessment", "oustanding assessments")}.
            Only completed assessments will be submitted.
          </h4>
          <p>
            Below are the assessments that will <b>not</b> be included in your submission:
          </p>
          <ul>
            {unsubmittableAssessments?.map((a) => {
              return <li key={a._id}>{a.name}</li>
            })}
          </ul>
        </Modal.Content>
        <Modal.Actions>
          <Button content="Cancel" onClick={() => setAssessmentSubmissionModalOpen(false)} />
          <Button
            primary
            icon="check"
            content="Submit only the completed assessments"
            disabled={submissableAssessments?.length === 0}
            onClick={() => {
              setAssessmentSubmissionModalOpen(false)
              submitAllAssessments()
            }}
          />
        </Modal.Actions>
      </Modal>
      <div className="assessment-tour-step-1">
        <AssessmentClosureInfo ideaTemplate={ownerIdeaTemplate} assessmentRound={assessmentRound} />
        <Table unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("common:capitalise", { key: "generic.idea" })}</Table.HeaderCell>
              {showBusinessProfile && <Table.HeaderCell>Business Profile</Table.HeaderCell>}
              <Table.HeaderCell>
                Total score
                <Popup
                  position="top right"
                  trigger={<Icon name="question circle" style={{ marginLeft: 5 }} />}
                  content="Automatically calculated based on your assessment & predefined section weightings"
                />
              </Table.HeaderCell>
              <Table.HeaderCell>Progress</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              {!theme.sizes.isMobile && <Table.HeaderCell />}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {assignedAssessments?.map((myAssessment, index) => {
              const assessmentIsSubmissable = util.assessmentIsSubmissable(myAssessment, ownerIdeaTemplate)
              const completedFields = Object.values(myAssessment?.assessment || {}).filter(
                (field: any) =>
                  (field.value !== undefined && field.value !== "") ||
                  (field.score !== undefined && field.score !== ""),
              ).length
              const progress = completedFields / totalAssessableFields

              return (
                <Table.Row
                  key={myAssessment.idea?._id + myAssessment.assessmentRoundId}
                  className={"assessment-tour-step-2"}
                >
                  <Table.Cell width="5">
                    <FakeLink
                      onClick={() => {
                        setAssessmentRound(assessmentRound?.id)
                        setPreviewId(myAssessment.idea?._id)
                      }}
                    >
                      {myAssessment.idea?.name}
                    </FakeLink>
                  </Table.Cell>
                  {showBusinessProfile && (
                    <Table.Cell>
                      <IdeaBusinessProfileChip
                        ideaBusinessProfile={myAssessment.idea.lockedOwnerIdeaBusinessProfile}
                      ></IdeaBusinessProfileChip>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    {myAssessment?.conflictOfInterest?.hasConflictOfInterest && myAssessment?.isSubmitted ? (
                      "N/A"
                    ) : (
                      <>{Math.round(myAssessment?.score || 0)} %</>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    {myAssessment?.conflictOfInterest?.hasConflictOfInterest ? (
                      <Label size="small" color="red" basic>
                        Conflict of interest
                      </Label>
                    ) : (
                      <div>
                        <Popup
                          trigger={
                            <span>
                              <CircularProgress
                                size={20}
                                color={progress === 1 ? "#28a745" : "#007bff"}
                                progress={progress}
                                strokeWidth={1.5}
                                icon={
                                  completedFields === totalAssessableFields ? <Icon name="check" color="green" /> : null
                                }
                              />
                            </span>
                          }
                          content={`${completedFields} / ${totalAssessableFields} questions completed`}
                        />
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {myAssessment?.isSubmitted ? (
                      <Label size="small" color="green">
                        Submitted
                      </Label>
                    ) : (
                      <Label size="small" color="orange">
                        Not submitted
                      </Label>
                    )}
                  </Table.Cell>
                  {!theme.sizes.isMobile && (
                    <Table.Cell>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <IdeaPdfExport
                          assessmentRound={assessmentRound}
                          ideas={[myAssessment.idea]}
                          challenge={challenge}
                          trigger={<Button size="tiny" basic icon="file pdf" />}
                        />
                        {!myAssessment?.isSubmitted && !assessmentClosed ? (
                          <TooltipButton
                            tooltip={
                              assessmentIsSubmissable
                                ? undefined
                                : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest and provide context of your conflict."
                            }
                            disabled={!assessmentIsSubmissable}
                            size="tiny"
                            inverted
                            primary
                            content="Submit"
                            onClick={() => submitAssessment(myAssessment?.idea?._id)}
                          />
                        ) : null}
                        <Button
                          primary={!myAssessment?.isSubmitted}
                          size="tiny"
                          content={
                            myAssessment?.isSubmitted ? `View ${t("generic.idea")}` : `Assess ${t("generic.idea")}`
                          }
                          className={index === 0 ? "assessment-tour-step-3" : null}
                          icon="eye"
                          onClick={() => {
                            setAssessmentRound(assessmentRound?.id)
                            setPreviewId(myAssessment.idea?._id)
                          }}
                        />
                      </div>
                    </Table.Cell>
                  )}
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginTop: 20,
        }}
      >
        <Popup
          trigger={
            <Button
              size="tiny"
              secondary
              basic
              content="Download summary"
              icon="file excel"
              onClick={() => downloadReport()}
            />
          }
          content="Generate an excel report containing a summary of your assessments"
        />
        <IdeaPdfExport
          ideas={currentRoundAssessmentIdeas as unknown as OpenAPI.Schemas["Idea"][]}
          assessmentRound={assessmentRound}
          hidePopup
          challenge={challenge as unknown as OpenAPI.Schemas["Challenge"]}
          trigger={<Button size="tiny" secondary basic content={`Download PDF`} icon="file pdf" />}
        />
        {unsubmittedAssessments?.length > 0 && (
          <TooltipButton
            icon="check"
            primary
            size="small"
            content="Submit all"
            className="assessment-tour-step-6"
            onClick={() => checkAllSubmissibility()}
          />
        )}
      </div>
    </>
  )
}

export default YourAssessmentRound
